import { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Field } from 'react-final-form';
import type { Location } from 'history';
import get from 'lodash/get';

import type { MessageTranslator } from '@oneflowab/pomes';

import workspacesReducer from '@app/reducers/entities/workspaces';

import * as EditableForm from '@app/components/editable-form';
import TextArea from '@app/components/text-area';
import TextField from '@app/components/text-field';
import SelectField from '@app/components/select-field';
import { checkAcl } from '@flow/libs/utils/acl';
import { DetailsFooter } from '@app/components/details-footer';
import { LocalizedDateTime } from '@app/components/localized-date-time';
import { T } from '@app/components/translation';
import { getWorkspaceTypesAsOptions, getWorkspaceType } from '@app/components/workspace-type';
import {
  WorkspaceContractsCount,
  WorkspaceTemplatesCount,
  WorkspaceUserAccess,
} from '@app/components/workspace-counts';

// eslint-disable-next-line import/named
import { hasAnyWorkspace } from '@flow/libs/oneflow-client/workspaces';

import adminPage from '@app/hocs/admin-page';

type Props = {
  message: MessageTranslator;
  location: Location;
};

type Params = {
  id: string;
};

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

type FormData = {
  name: string;
  type: Option | Oneflow.Workspace['type'];
  description?: string;
};

export const WorkspaceDetailsComponent = ({ message, location }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: workspaceId } = useParams<Params>();
  const numericId = Number(workspaceId);

  const workspace = useSelector((state) =>
    workspacesReducer.getWorkspaceSelector(state, {
      id: numericId,
    }),
  );
  const updateState = useSelector((state) =>
    workspacesReducer.getUpdateSelector(state, {
      id: numericId,
    }),
  );

  const [isEditMode, setIsEditMode] = useState(false);

  const resetUpdateState = () => {
    dispatch(
      workspacesReducer.updateWorkspaceReset({
        id: numericId,
      }),
    );
  };
  const updateWorkspace = (data: FormData) => {
    const { name, type, description } = data;
    dispatch(
      workspacesReducer.updateWorkspace({
        id: numericId,
        data: {
          name,
          type: get(type, 'value') || type,
          description: description || null,
        },
        pipe: {
          onSuccess: () => {
            setIsEditMode(false);
          },
        },
      }),
    );
  };

  const checkEditPermission = useCallback(
    () =>
      checkAcl(
        workspace.acl,
        [
          'collection:update:type',
          'collection:update:name',
          'collection:update:description',
        ],
        {
          match: 'any',
        },
      ),
    [
      workspace.acl,
    ],
  );

  useEffect(() => {
    if (!isEditMode && location?.query?.edit && checkEditPermission()) {
      history.replace(location.pathname);
      setIsEditMode(true);
    }
  }, [
    isEditMode,
    location,
    checkEditPermission,
    history,
  ]);

  const getInitialValues = () => {
    const { name, type, description } = workspace;
    return {
      name,
      type,
      description,
    };
  };

  const handleEditMode = (editMode: boolean) => {
    if (updateState.pristine) {
      resetUpdateState();
    }
    setIsEditMode(editMode);
  };

  const uniqueWorkspaceName = ({ params }: { params: any }) =>
    hasAnyWorkspace({
      params,
      entityId: workspace.id,
    });

  const getPlaceholder = (placeholder: string, value: string) => {
    if (isEditMode) {
      return placeholder;
    }
    return value || '-';
  };

  if (!workspace || !workspace.acl) {
    return null;
  }

  const containsValue = (
    <div>
      <WorkspaceContractsCount workspace={workspace} />
      <br />
      <WorkspaceTemplatesCount workspace={workspace} />
    </div>
  );

  const infoPairs = [
    {
      label: (
        <T
          _str="ID"
          _comment="ID label for the details footer"
        />
      ),
      value: workspace.id,
    },
    {
      label: (
        <T
          _str="Created"
          _comment="Created date label for the details footer"
        />
      ),
      value: <LocalizedDateTime datetime={workspace.createdTime} />,
    },
    {
      label: (
        <T
          _str="Contains"
          _comment="Document and template counts for the details footer"
        />
      ),
      value: containsValue,
    },
    {
      label: (
        <T
          _str="Access"
          _comment="Access count for the details footer"
        />
      ),
      value: <WorkspaceUserAccess workspace={workspace} />,
    },
  ];

  return (
    <>
      <Form
        initialValues={getInitialValues()}
        onSubmit={updateWorkspace}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <EditableForm.Header>
              <EditableForm.Actions
                resetFormState={resetUpdateState}
                formState={updateState}
                isEditMode={isEditMode}
                setIsEditMode={handleEditMode}
                disabled={!checkEditPermission()}
              />
            </EditableForm.Header>
            <EditableForm.Body>
              <EditableForm.FormItem>
                <EditableForm.Label>
                  <T
                    _str="Name"
                    _comment="Used as the label of the name field on the workspace details page."
                  />
                  <EditableForm.RequiredAsterisk isEditMode={isEditMode} />
                </EditableForm.Label>
                <Field
                  name="name"
                  component={TextField}
                  maxLength={50}
                  placeholder={message({
                    id: 'Enter name',
                    comment: 'Placeholder for the name field on the workspace details page',
                  })}
                  unique={{
                    text: message({
                      id: 'A workspace with this name already exists. Try another one.',
                      comment: 'Uniqueness error message on the workspace details page',
                    }),
                    param: 'name',
                    request: uniqueWorkspaceName,
                    initialValue: workspace.name,
                  }}
                  required
                  autoFocus
                  value={workspace.name}
                  disabled={!isEditMode || !checkAcl(workspace.acl, 'collection:update:name')}
                />
              </EditableForm.FormItem>
              <EditableForm.FormItem>
                <EditableForm.Label>
                  <T
                    _str="Type"
                    _comment="Used as the label of the type field on the workspace details page."
                  />
                  <EditableForm.RequiredAsterisk isEditMode={isEditMode} />
                </EditableForm.Label>
                <Field
                  name="type"
                  component={SelectField}
                  options={getWorkspaceTypesAsOptions(message)}
                  placeholder={message({
                    id: 'Select type',
                    comment: 'Placeholder for the type field on the workspace details page',
                  })}
                  clearable={false}
                  searchable={false}
                  required
                  disabled={!isEditMode || !checkAcl(workspace.acl, 'collection:update:type')}
                  value={getWorkspaceType(workspace.type, message)}
                />
              </EditableForm.FormItem>
              <EditableForm.FormItem>
                <EditableForm.Label>
                  <T
                    _str="Description"
                    _comment="Used as the label of the description field on the workspace details page."
                  />
                </EditableForm.Label>
                <Field
                  name="description"
                  placeholder={getPlaceholder(
                    message({
                      id: 'Enter description',
                      comment:
                        'Placeholder for the description field on the workspace details page',
                    }),
                    workspace.description,
                  )}
                  value={workspace.description || '-'}
                  disabled={
                    !isEditMode || !checkAcl(workspace.acl, 'collection:update:description')
                  }
                  component={TextArea}
                  maxLength={300}
                />
              </EditableForm.FormItem>
            </EditableForm.Body>
          </form>
        )}
      />
      <DetailsFooter infoPairs={infoPairs} />
    </>
  );
};

export default adminPage(({ props: { message } }) => ({
  title: message({
    id: 'Details',
    comment: 'Used as the page title of the workspace details page',
  }),
  modules: [
    [],
  ],
}))(WorkspaceDetailsComponent);
